/**
 * ImpactWarning — async-loads the downstream impact for an asset and
 * renders a compact warning block. Intended for use inside delete
 * confirmation dialogs so users see "you're about to break N things"
 * at the exact moment they're about to make a destructive change.
 *
 * Usage:
 *   <ImpactWarning kind="model" id={modelId} />
 *
 * On mount it calls `/api/lineage/impact/{kind}/{id}`. Renders nothing
 * if the asset has zero downstream consumers (silent = safe).
 */

import React, { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { AlertTriangle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function ImpactWarning({ kind, id, className = '' }) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!kind || !id) { setLoading(false); return; }
    apiFetch(`${API}/api/lineage/impact/${kind}/${encodeURIComponent(id)}`, { method: 'GET' })
      .then(({ ok, data }) => {
        if (cancelled) return;
        if (ok) setImpact(data);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind, id]);

  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-[10px] text-muted-foreground/80 ${className}`} data-testid="impact-warning-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Checking dependencies…
      </div>
    );
  }
  if (!impact || impact.error) return null;
  if ((impact.total_consumers || 0) === 0) {
    return (
      <div className={`text-[10px] text-emerald-400/80 ${className}`} data-testid="impact-warning-safe">
        ✓ No downstream consumers — safe to delete.
      </div>
    );
  }

  return (
    <div className={`mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-[10px] leading-relaxed ${className}`} data-testid="impact-warning">
      <div className="flex items-start gap-1.5">
        <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
        <span className="font-semibold text-amber-300">{impact.summary}</span>
      </div>
    </div>
  );
}

export default ImpactWarning;
